import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  Renderer2,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewEncapsulation,
  HostListener,
} from '@angular/core';
import {
  Store,
  Select,
  Actions,
  ofActionDispatched,
  ofActionCompleted,
} from '@ngxs/store';
import { ActivatedRoute } from '@angular/router';
import { Subject, Observable, combineLatest, fromEvent, merge } from 'rxjs';
import { ViewService } from '../../view.service';
import { EnginePositionModel } from 'src/app/engine/models/engine-position.model';
import { EngineVisibilityEnum } from 'src/app/engine/models/engine-visibility.enum';
import {
  SetEnginePosition,
  ClickNode,
  SetDisplay,
  SetVisible,
  SetAutoplay,
  ToggleProgressBar,
  PlayAudio,
  SetActiveTool,
  ToggleLabels,
} from 'src/app/engine/state/engine.actions';
import { ViewState } from '../../state/view.state';
import {
  takeUntil,
  map,
  filter,
  debounceTime,
  pairwise,
  take,
  distinctUntilChanged,
  skip,
  tap,
} from 'rxjs/operators';
import { ViewActions } from '../../state/view.actions';
import { ViewUIStatus } from '../../models/view-ui-status.enum';
import { ViewUIModel } from '../../models/view-ui.model';
import { ToolEnum } from 'src/app/engine/models/tool.enum';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { ViewMode } from '../../models/view-mode.enum';
import * as Mousetrap from 'mousetrap';
import * as resizeDetectorMaker from 'element-resize-detector';
import { Fullscreen } from 'src/app/shared/libraries/fullscreen/fullscreen';
import { EngineProgressState } from 'src/app/engine/state/progress/engine.progress.state';
import {
  SetLabelsCollapsed,
  SetLabelsDraggable,
  EnableContextLabel,
} from 'src/app/story/state/labels/story.labels.actions';
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';
import { ViewToolsMode } from '../../models/view-tools-mode.enum';
import { InteractTypeEnum } from '../../models/interact-type.enum';
import { isNil, get } from 'lodash';
import { ViewUIService } from '../../view-ui.service';
import { ViewUI } from '../../models/view-ui.enum';
import { ViewDrawerComponent } from '../../components/view-drawer/view-drawer.component';
import { LabelCollapseModeEnum } from 'src/app/story/models/labels/label-collapse-mode';
import { ResetProgress, SetProgress } from 'src/app/engine/state/progress/engine.progress.actions';
import { EngineService } from 'src/app/engine/engine.service';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { BreakpointState } from 'src/app/breakpoint/state/breakpoint.state';
import { Breakpoint } from 'src/app/breakpoint/models/breakpoint.enum';
import { ChapterModel } from 'src/app/story/models/chapter.model';
import { ViewWindow } from '../../models/view-window.enum';
import { StoryState } from 'src/app/story/state/story.state';
import { EngineNodesState } from 'src/app/engine/state/nodes/engine.nodes.state';
import { Title } from '@angular/platform-browser';
import { ViewLoaderEnum } from '../../models/view-loader.enum';
import { BackgroundModel } from 'src/app/engine/models/background.model';
import { ColorUtils } from 'src/app/shared/libraries/colors/colors';
import { ViewInputModeEnum } from '../../models/view-input-mode.enum';
import { HelpTypeEnum } from '../../models/help-type.enum';
import { MediaModel } from 'src/app/story/models/media.model';
import { NotificationModel } from 'src/app/shared/features/notification/notification.model';
import { ChapterReadyStateModel } from 'src/app/story/models/chapter-ready-state.model';
import { LoadStoryChunkSuccess } from 'src/app/story/state/story.actions';
import { LayoutState } from 'src/app/layout/state/layout.state';
import { environment } from 'src/environments/environment';
import { EnvironmentApplication } from 'src/environments/models/environment-application';
import { ContentType } from 'src/app/story/models/content-type.enum';
import { NativeEnvironmentPlatform } from 'src/environments/models/native-environment-platform';
import { QuizService } from '../../components/quiz/view-quiz.service';
import { PendoService } from 'src/app/shared/pendo.service';
import { QuizActions } from 'src/app/quiz/state/quiz.actions';
import { QuizState } from 'src/app/quiz/state/quiz.state';
import { SetContentViewed } from 'src/app/auth/state/auth/auth.actions';
import { AuthState } from 'src/app/auth/state/auth/auth.state';
import { LiveAnnouncer } from '@angular/cdk/a11y';

const drawerHeight = 64;

@Component({
  selector: 'app-view-page',
  templateUrl: './view-page.component.html',
  styleUrls: ['./view-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ViewPageComponent implements OnInit, AfterViewInit, OnDestroy {
  application = EnvironmentApplication;
  nativePlatform = NativeEnvironmentPlatform;

  // ---------- Child Elements ----------
  @ViewChild('engineContainer', { static: true })
  engineContainer: ElementRef;

  @ViewChild('viewContainer', { static: true })
  viewContainer: ElementRef;

  @ViewChild('view', { static: true })
  view: ElementRef;

  @ViewChild('viewCameraBar')
  viewCameraBar: ElementRef;

  @ViewChild('viewLogo')
  viewLogo: ElementRef;

  @ViewChild('viewSceneBase', { static: true })
  viewSceneBase: ElementRef;

  @ViewChild('viewToolbar')
  viewToolbar: ElementRef;

  @ViewChild('drawer', { read: ViewDrawerComponent })
  drawer: ViewDrawerComponent;

  // ---------- Observables ----------

  @Select(StoryState.getStoryId)
  storyId$: Observable<string>;

  @Select(ViewState.getUI)
  layout$: Observable<ViewUIModel>;

  @Select(ViewState.getInputEnabled)
  inputEnabled$: Observable<boolean>;

  @Select(ViewState.getFullscreenActive)
  fullscreenActive$: Observable<boolean>;

  @Select(ViewState.getDrawerCoversContent)
  drawerCoversContent$: Observable<boolean>;

  @Select(ViewState.getInteractType)
  interactType$: Observable<InteractTypeEnum>;

  @Select(ViewState.getOnDemand)
  onDemand$: Observable<boolean>;

  @Select(ViewState.getShowHelpTab)
  showHelpTab$: Observable<HelpTypeEnum>;

  @Select(ViewState.getUsePrintImage)
  usePrintImage$: Observable<boolean>;

  @Select(ViewState.getOpenWindow)
  viewWindow$: Observable<ViewWindow>;

  @Select(ViewState.getTutorialSeen)
  tutorialSeen$: Observable<Boolean>;

  @Select(EngineProgressState.getStoryLoadProgress)
  loadProgress$: Observable<number>;

  @Select(BreakpointState.getBreakpoint)
  breakpoint$: Observable<Breakpoint>;

  @Select(StoryChaptersState.getSelectedChapter)
  chapter$: Observable<ChapterModel>;

  @Select(StoryChaptersState.getSelectedChapterReadyState)
  selectedChapterReady$: Observable<ChapterReadyStateModel>;

  @Select(StoryChaptersState.getChaptersLoaded)
  chaptersLoaded$: Observable<any>;

  @Select(StoryChaptersState.getSelectedChapterBackground)
  background$: Observable<BackgroundModel>;

  @Select(StoryChaptersState.getSelectedChapterAudio)
  audio$: Observable<MediaModel>;

  

  // ---------- Class Variables ----------
  breakpoint = Breakpoint;
  viewLayoutStatus = ViewUIStatus;
  viewMode = ViewMode;
  viewUI = ViewUI;
  viewWindow = ViewWindow;
  columnLayoutStatus = ViewUIStatus.Hide;
  drawerExpandable = true;
  drawerOpen = false;
  drawerY = 0;
  loadLabel = '';
  loadProgress = 0;
  chaptersLoaded = 0;
  isBackgroundLight = true;
  selectedChapterLoading = false;
  viewToolsMode = ViewToolsMode;
  notification: NotificationModel;

  audioVisible = false;
  cameraVisible = false;
  columnVisible = false;
  drawerVisible = false;
  logoVisible = false;
  menuVisible = false;
  nodeInfoVisible = false;
  searchVisible = false;
  titleOnlyVisible = false;
  toolbarVisible = false;
  undoVisible = false;

  contentId: string;
  contentType: ContentType;
  contentLang: string;

  private ngUnsubscribe = new Subject();
  private audioListening = new Subject();

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent) {
    this.handleFirstClickTracking(event);
  }

  // ---------- Lifecycle hooks ----------
  constructor(
    private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    private changeDetectorRef: ChangeDetectorRef,
    private store: Store,
    private actions: Actions,
    private liveAnnouncer: LiveAnnouncer,
    private viewService: ViewService,
    private quizService: QuizService,
    private layoutService: ViewUIService,
    private engineService: EngineService,
    private pendoService: PendoService,
    private title: Title
  ) {}

  ngOnInit() {
    // Reset scene visibility so we don't see a FOUC of the last scene
    // This must happen before any meaningful browser render
    this.resetScene();

    const queryParams = this.viewService.normalizeQueryParams(
      this.activatedRoute.snapshot.queryParams
    );

    this.contentId = queryParams.id;
    this.contentType = queryParams.type;
    this.contentLang = queryParams.lang;

    this.initContentViews();
  }

  ngAfterViewInit() {
    this.initOnDemand();

    this.store.dispatch(
      new SetLabelsDraggable({
        tag: true,
        pin: false,
      })
    );

    // Enable autoplay animations
    this.store.dispatch(new SetAutoplay(true));

    // set loaders
    const viewLoader = this.store.selectSnapshot(ViewState.getUI).loader
      .template;

    // set engine progress bar
    const showBar = viewLoader === ViewLoaderEnum.Bar;
    this.store.dispatch(new ToggleProgressBar(showBar));

    // set progress window open
    if (viewLoader !== ViewLoaderEnum.None) {
      this.store.dispatch(
        new ViewActions.ToggleWindow(ViewWindow.Loader, true)
      );
    }

    this.checkCapabilities();
    this.initSizeObservers();
    this.initHeaderObserver();
    this.initLayoutObserver();
    this.initBreakpointObservers();
    this.initKeyboardObserver();
    this.initLoadProgressObserver();
    this.initWindowObserver();
    this.initBackgroundColorObserver();
    this.initResponsiveLabels();
    this.initNodeObserver();
    this.initChapterObserver();
    this.initAudioObserver();
    this.initNotifications();

    this.initAccessibility();

    this.positionRelativeElements(this.drawerY);

    this.storyId$
      .pipe(
        filter((storyId) => {
          // Eliminate 'preview' mode from Pendo data gathering
          return !isNil(storyId) && storyId !== 'preview';
        }),
        take(1),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => this.initPendoEvents());
  }

  ngOnDestroy() {
    this.store.dispatch(new SetDisplay(false));
    // this.store.dispatch(new NodeActions.ResetEngineNodesLegacy());
    this.store.dispatch(new ResetProgress());

    if (Fullscreen.isFullscreen()) {
      Fullscreen.exitFullscreen();
    }

    // exit quiz
    this.quizService.exitQuiz();

    this.unbindKeyboard();

    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initContentViews() {
    if (environment.application === EnvironmentApplication.Studio) {
      combineLatest([
        this.store.select(AuthState.isPremiumUser),
        this.store.select(StoryChaptersState.getSelectedChapterModelReady),
        this.store.select(AuthState.getContentViews)
      ])
      .pipe(
        filter(([
          isPremiumUser, 
          selectedChapterReady, 
          contentViews
        ]) => !isPremiumUser && selectedChapterReady && !contentViews.limit_reached),
        take(1),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.store.dispatch(new SetContentViewed(this.contentId, this.contentType));
      });
    }
  }

  initOnDemand() {
    this.onDemand$
      .pipe(
        filter((onDemand) => !onDemand),
        take(1),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        // Load content.
        this.viewService.initStory(
          this.contentId,
          this.contentType,
          this.contentLang
        );
      });
  }

  // ---------- Capability detection ----------
  checkCapabilities() {
    const mouseOver = this.renderer.listen('window', 'mouseover', () => {
      const canHover = true;
      mouseOver();

      this.store.dispatch(new ViewActions.SetCanHover(canHover));
    });

    const canTouch =
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.msMaxTouchPoints > 0;

    this.store.dispatch(new ViewActions.SetCanTouch(canTouch));
    this.store.dispatch(
      new ViewActions.SetCanFullscreen(Fullscreen.canFullscreen())
    );

    // set default input mode
    this.store.dispatch(
      new ViewActions.SetInputMode(
        canTouch ? ViewInputModeEnum.Touch : ViewInputModeEnum.Mouse
      )
    );
  }

  // ---------- Listeners ----------
  initBreakpointObservers() {
    this.breakpoint$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.handleResize();
    });
  }

  initResponsiveLabels() {
    // observer: window size change (throttle)
    const windowResize$ = fromEvent(window, 'resize').pipe(debounceTime(100));

    // observe: breakpoint changes
    const labelBreakpoint$ = this.store.select(ViewState.getLabelsBreakpoint);

    // observe: label selection/de-selection
    const labelSelected$ = this.store.select(StoryLabelsState.getSelectedLabel);

    // observe: label collapse state
    const labelsCollapsed$ = this.store.select(
      StoryLabelsState.getLabelsCollapsed
    );

    // set: label info on selection
    labelSelected$
      .pipe(
        // ignore if not collapsed
        filter(() =>
          this.store.selectSnapshot(StoryLabelsState.getLabelsCollapsed)
        ),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((label) => {
        if (label) {
          this.viewService.toggleLabelInfo(true);
        } else {
          this.viewService.toggleLabelInfo(false);
        }
      });

    // set: label info on collapse
    labelsCollapsed$
      .pipe(
        // validate selected label
        filter(
          () => !!this.store.selectSnapshot(StoryLabelsState.getSelectedLabel)
        ),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((collapsed) => {
        this.viewService.toggleLabelInfo(collapsed);
      });

    // combine various label breakpoint triggers
    combineLatest(windowResize$, labelBreakpoint$)
      .pipe(
        // ignore unless breakpoint enabled
        filter(([, labelBreakpoint]) => !isNil(labelBreakpoint)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.checkLabelsCollapsed();
      });

    // set initial
    this.checkLabelsCollapsed();
  }

  initSizeObservers() {
    const resizeDetector = resizeDetectorMaker({ strategy: 'scroll' });

    // Listen to view page resize
    const windowResize$ = fromEvent(window, 'resize');
    windowResize$
      .pipe(debounceTime(10), takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.updateViewHeight();
        this.refreshLayout();
      });

    // Listen to engine container resize
    const engineResize$ = new Observable((subscriber) => {
      resizeDetector.listenTo(this.engineContainer.nativeElement, () => {
        subscriber.next();
      });
    });

    engineResize$.pipe(debounceTime(10)).subscribe(() => {
      this.resizeEngineContainer();
    });
  }

  initHeaderObserver() {
    this.store
      .select(LayoutState.getSiteHeader)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((header) => {
        this.updateViewHeight();
      });
  }

  initLayoutObserver() {
    this.layout$
      .pipe(
        filter((layout) => layout != null),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((layout) => {
        this.refreshLayout();
      });
  }

  initKeyboardObserver() {
    this.inputEnabled$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((enabled) => {
        enabled ? this.bindKeyboard() : this.unbindKeyboard();
      });
  }

  initNodeObserver() {
    this.actions
      .pipe(ofActionDispatched(ClickNode), takeUntil(this.ngUnsubscribe))
      .subscribe((action: ClickNode) => {
        this.viewService.delegateNodeClick(action.node, action.event);
      });

    this.store
      .select(EngineNodesState.getSelectedNode)
      .pipe(pairwise(), takeUntil(this.ngUnsubscribe))
      .subscribe(([lastSelected, currentSelected]) => {
        const lastSelectedId = get(lastSelected, 'objectId', undefined);
        const currentSelectedId = get(currentSelected, 'objectId', undefined);

        if (
          currentSelected === undefined ||
          lastSelectedId !== currentSelectedId
        ) {
          this.viewService.toggleNodeInfo(false);
        }
      });
  }

  initChapterObserver() {
    // Chapter change
    this.store
      .select(StoryChaptersState.getSelectedChapterId)
      .pipe(
        filter((chapter) => chapter != null),
        map(() =>
          this.store.selectSnapshot(StoryChaptersState.getSelectedChapter)
        ),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((chapter) => {
        this.viewService.initChapter(chapter);
        this.setPageTitle(chapter);
        this.refreshLayout();
      });
  }

  initLoadProgressObserver() {
    // update the loader with the percentage of assets loaded
    this.store
      .select(EngineProgressState.getChunkLoadProgress)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((progress) => {
        this.loadProgress = progress * 100;
        this.engineService.fireEvent(EventsEnum.HumanProgress, {
          progress: this.loadProgress,
        });
        this.changeDetectorRef.markForCheck();
        this.checkShowLoader();
      });

    // hide the loader when the currently selected chapter is done processing
    this.store
      .select(StoryState.getLoadingChunk)
      .pipe(
        filter(() => this.store.selectSnapshot(StoryChaptersState.getSelectedChapterId) != null),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((chunk) => {
        const showLoader =
          chunk !== null &&
          chunk.chapterId ===
            this.store.selectSnapshot(StoryChaptersState.getSelectedChapterId);

        if (!showLoader && !environment.offline) {
          this.engineService.enableRender();
          this.store.dispatch(
            new ViewActions.ToggleWindow(ViewWindow.Loader, false)
          );
          this.refreshLayout();

          // #FIXME [GH] we should only start animating AFTER the circle loader has gone away, and the UI has come up, and the canvas resizes itself.
          // this should likely be triggered by an event generated when the canvas is done resizing itself to avoid adding this magic number 
          const animationDelay = 500;
          setTimeout(() => {
            this.engineService.enableAnimation();
          }, animationDelay);
        } else {
          this.store.dispatch(
            new ViewActions.ToggleWindow(ViewWindow.Loader, true)
          );
        }
      });

    // There are story load chunks that don't actually initiate any downloading.
    // Therefore, we can only know when loading has completed by pipeing LoadStoryChunkSuccess, and there are zero chunks remaining
    // In case the hide code didn't get called previously hide it now
    this.actions
      .pipe(
        ofActionCompleted(LoadStoryChunkSuccess),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        const chunksRemaining = this.store.selectSnapshot(
          StoryState.getLoadChunksRemaining
        ).length;
        if (chunksRemaining === 0) {
          this.store.dispatch(
            new ViewActions.ToggleWindow(ViewWindow.Loader, false)
          );
          if (environment.offline) {
            // #FIXME [GH] the offline environments are much slower to load and should
            // only start animating AFTER the model is completely loaded, plus a significant
            // delay for the UI to load and the canvas resize.
            // This should likely be triggered by an event generated when the canvas is done
            // resizing itself to avoid adding this magic number 
            const animationDelay = 1750;
            setTimeout(() => {
              this.engineService.enableAnimation();
            }, animationDelay);
            // log a timing marker for performance monitoring
            // console.log(new Date().toISOString() + ' ** model fully loaded');
          }
        }
      });
  }

  // edge case:  it is possible to select a chapter during loading
  //   show the loader if the selected chapter is loading
  checkShowLoader() {
    const selectedChapterIndex = this.store.selectSnapshot(
      StoryChaptersState.getSelectedChapterIndex
    );

    if (selectedChapterIndex > 0) {
      const selectedChapterId = this.store.selectSnapshot(
        StoryChaptersState.getSelectedChapterId
      );
      const currentChapter = this.store.selectSnapshot(
        StoryState.getLoadingChunk
      );
      if (
        currentChapter !== null &&
        selectedChapterId === currentChapter.chapterId
      ) {
        this.store.dispatch(
          new ViewActions.ToggleWindow(ViewWindow.Loader, true)
        );
      }
    }
  }

  initWindowObserver() {
    this.viewWindow$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((window) => {
      this.viewService.resetLayout();

      if (window === ViewWindow.Tutorial) {
        this.viewService.resetChapter();
        this.viewService.toggleLabels(false);
      } else {
        this.viewService.toggleLabels(true);
      }
    });
  }

  initBackgroundColorObserver() {
    this.background$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((background) => {
        if (!background.colors) {
          return;
        }

        const outerRGB = background.colors.slice(0, 3);
        this.isBackgroundLight = ColorUtils.isLight([
          outerRGB[0],
          outerRGB[1],
          outerRGB[2],
        ]);
        this.changeDetectorRef.markForCheck();
      });
  }

  initAudioObserver() {
    const stopAudio = merge(this.ngUnsubscribe, this.audioListening);

    // get computed tutorial seen
    const tutorialInteractSeen$ = this.viewWindow$.pipe(
      // map to current seen state
      map(
        (window) =>
          window === ViewWindow.Tutorial || window === ViewWindow.Interact
      ),
      // take once when true
      filter((seen) => seen),
      take(1)
    );

    // tap into latest state of each variable
    combineLatest(this.viewWindow$, tutorialInteractSeen$, this.audio$)
      .pipe(
        filter(([window]) => isNil(window)), // wait til window null
        takeUntil(stopAudio)
      )
      .subscribe(([, tutorialInteractSeen, audio]) => {
        if (tutorialInteractSeen) {
          if (!isNil(audio)) {
            this.playAudio();
          }
        } else {
          this.stopAudioListening();
        }
      });
  }

  initNotifications() {
    this.notification = new NotificationModel(
      'Welcome! Learn how to navigate and interact with this model in 3D.',
      'Launch Tutorial'
    );
  }

  initPendoEvents() {
    
    // Initialize shared metadata. Useful to give question events context around quiz size, and
    // potential future values. 
    const metadata = {
      totalQuestions: 0
    };

    this.store.select(QuizState.getQuestionTotal)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(total => {
        metadata.totalQuestions = total;
      });
    

    // Listen for quiz start only for now. Quiz exit requires conditional logic to handle
    // page exit, resets, state being cleared too early
    this.store.select(QuizState.getQuizModeActive)
      .pipe(
        filter(() => this.store.selectSnapshot(QuizState.getQuestionTotal) > 0),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(active => {
        if (active) {
          this.pendoService.track('Quiz - Entered', metadata);
        }
      });
    
    // Listen for first and last answers. These track whether the user has 
    // started and finished the quiz. We track first question submitted
    // because it's a more reliable metric that the user is actually engaging in the quiz.
    this.actions.pipe(
      ofActionDispatched(QuizActions.SubmitQuestionAnswer),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(() => {
      const index = this.store.selectSnapshot(QuizState.getActiveQuestionIndex);
      const questionsRemaining = this.store.selectSnapshot(QuizState.getQuestionsRemaining);
      

      if (index === 0) {
        this.pendoService.track('Quiz - First Question Answered', metadata);
      }

      if (questionsRemaining === 0) {
        this.pendoService.track('Quiz - Completed', metadata);
      }
    });

    // Listen for content loaded.
    this.store.select(StoryChaptersState.getSelectedChapterModelReady)
      .pipe(
        filter((ready) => ready),
        take(1),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        const storyId = this.store.selectSnapshot(StoryState.getStoryId);
        const story = this.store.selectSnapshot(StoryState.getStory);        

        this.pendoService.trackOncePerPage('View - Loaded', {
          id: storyId,
          chapters: story.chapters.length,
          tour: story.tags.includes('tour'),
          animated: story.tags.includes('animated'),
          quiz: this.store.selectSnapshot(QuizState.hasQuiz),
          edit: false
        });
      });
  }

  initAccessibility() {
    // Handle chapter change event
    this.chapter$.pipe(
      filter(chapter => chapter !== null),
      distinctUntilChanged((a, b) => a.id === b.id),
      map(chapter => get(chapter, 'info.displayName', '')),
    ).subscribe(displayName => {
      // Announce chapter has changed
      this.liveAnnouncer.announce(`Opened chapter ${displayName}`);
    });
  }

  handleFirstClickTracking(event: MouseEvent) {
    // Track where the click came from. Since we're tracking "view" interaction,
    // exlude any events from the top header, which we consider outside the view.
    const paths = event.composedPath();
    const isHeaderClick = paths.some((el: HTMLElement) => {
      return el.nodeName === 'APP-SITE-HEADER';
    });
    
    if (isHeaderClick) {
      return;
    }

    // Check that the content has loaded so we only track interactions after
    // they are actually possible
    const ready = this.store.selectSnapshot(EngineProgressState.getStoryLoadProgress);

    // Track the first interaction, so we filter users that interacted in any way with the viewer
    if (ready === 1) {
      this.pendoService.trackOncePerPage('First Click');
    }
  }

  handleNotificationClose({ action }: { action: string }) {
    if (action === 'Launch Tutorial') {
      this.viewService.toggleTutorial(true);
    }

    // No action, notification was dismissed. Assume the user has seen the message
    // and is not interested in the tutorial.
    if (!action) {
      this.store.dispatch(new ViewActions.TutorialSeen(true));
    }
  }

  private stopAudioListening() {
    this.audioListening.next();
    this.audioListening.complete();
  }

  private playAudio() {
    this.store.dispatch(new PlayAudio(true));
    this.stopAudioListening();
  }

  // ---------- Event Handlers ----------
  handleColumnToggle() {
    this.viewService.toggleColumn();
    this.changeDetectorRef.markForCheck();
  }

  handleDrawerOffsetChange(drawerOffsetY: number) {
    this.drawerY = drawerOffsetY;
    this.drawerOpen = drawerOffsetY !== -drawerHeight;

    this.positionRelativeElements(this.drawerY);
  }

  checkLabelsCollapsed() {
    const breakpoint = this.store.selectSnapshot(BreakpointState.getBreakpoint);
    const labelBreakpoint = this.store.selectSnapshot(
      ViewState.getLabelsBreakpoint
    );
    const collapsed = this.isLabelBreaking(labelBreakpoint);

    // get collapsed mode: based on view breakpoint
    const collapsedMode =
      breakpoint >= Breakpoint.Large
        ? LabelCollapseModeEnum.Toggle
        : LabelCollapseModeEnum.None;

    this.store.dispatch(new SetLabelsCollapsed(collapsed, collapsedMode));
  }

  isLabelBreaking(labelBreakpoint: number): boolean {
    return window.innerWidth < labelBreakpoint;
  }

  bindKeyboard() {
    // Tool switching
    Mousetrap.bind('1', () => this.viewService.selectTool(ToolEnum.Select));
    Mousetrap.bind('2', () => this.viewService.selectTool(ToolEnum.Hide));
    Mousetrap.bind('3', () => this.viewService.selectTool(ToolEnum.Fade));

    // Column toggle
    Mousetrap.bind(['command+,', 'ctrl+,'], (event) => {
      event.preventDefault();

      const breakpoint = this.store.selectSnapshot(
        BreakpointState.getBreakpoint
      );
      breakpoint > Breakpoint.Medium
        ? this.viewService.toggleColumn()
        : this.drawer.toggleDrawer();

      this.changeDetectorRef.markForCheck();
    });

    // Presentation toggle
    Mousetrap.bind(['command+enter', 'ctrl+enter', 'command+p'], (event) => {
      event.preventDefault();
      Fullscreen.toggleFullscreen();
    });

    // Undo
    Mousetrap.bind('meta+z', (event) => {
      event.preventDefault();
      this.viewService.undo();
    });

    // Redo
    Mousetrap.bind(['meta+y', 'meta+shift+z'], (event) => {
      event.preventDefault();
      this.viewService.redo();
    });

    // Chapter browser
    Mousetrap.bind(['.', 'pagedown'], () =>
      this.viewService.nextChapter('Keyboard')
    );
    Mousetrap.bind([',', 'pageup'], () =>
      this.viewService.previousChapter('Keyboard')
    );

    // Delete
    Mousetrap.bind(['del', 'backspace'], (event) => {
      event.preventDefault();
      this.viewService.hideSelectedStructures();
    });

    // Reset
    Mousetrap.bind('R', (event) => {
      event.preventDefault();
      this.viewService.resetChapter();
    });

    // Seach
    Mousetrap.bind('/', () => this.viewService.toggleSearch());

    // Help
    Mousetrap.bind('?', () => this.viewService.toggleHelp());

    // Play / Pause
    Mousetrap.bind('space', (event) => {
      event.preventDefault();
      this.viewService.toggleAnimation();
    });
  }

  unbindKeyboard() {
    Mousetrap.reset();
  }

  // ---------- Layout ----------
  resizeEngineContainer() {
    const {
      top,
      left,
      width,
      height,
    } = this.engineContainer.nativeElement.getBoundingClientRect();

    // const { offsetLeft, offsetTop, offsetWidth, offsetHeight } = this.engineContainer.nativeElement;
    const position = new EnginePositionModel(
      left,
      top,
      width,
      height,
      EngineVisibilityEnum.Shown
    );

    this.store.dispatch(new SetEnginePosition(position));
  }

  // Mobile devices have variable viewheight because the browser bar can scroll in and out of view
  // This workaround determines the available window space and uses that as a viewheight unit
  updateViewHeight() {
    const includeOffset =
      environment.application !== EnvironmentApplication.Viewer;
    const vh = this.viewService.getVH(includeOffset);
    this.view.nativeElement.style.setProperty('--vh', `${vh}px`);
  }

  handleResize() {
    this.viewService.resetLayout();
  }

  // Previous scene is kept in memory so we don't have to reinitialize the entire engine every pageload.
  // We also want to keep the scene fully in memory so that transitions from /view to /edit don't require a reload.
  // This function hides the engine, waits until first meaningful render, then shows it, guaranteeing that the content
  // the user sees is accurate.
  resetScene() {
    this.store.dispatch(new SetDisplay(true));
    this.store.dispatch(new SetVisible(false));
    this.store.dispatch(new SetActiveTool(ToolEnum.Select));
    this.store.dispatch(new ResetProgress());

    this.engineService
      .getEvent(EventsEnum.AssetsLoadProgress)
      .pipe(take(1), takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.store.dispatch(new SetVisible(true));
      });
  }

  refreshLayout() {
    const layout = this.store.selectSnapshot(ViewState.getUI);

    // Update visible elements
    this.columnLayoutStatus = layout.column.status;

    this.audioVisible = this.layoutService.isVisible(ViewUI.Audio);
    this.cameraVisible = this.layoutService.isVisible(ViewUI.Camera);
    this.columnVisible = this.layoutService.isVisible(ViewUI.Column);
    this.drawerVisible = this.layoutService.isVisible(ViewUI.Drawer);
    this.logoVisible = this.layoutService.isVisible(ViewUI.Logo);
    this.menuVisible = this.layoutService.isVisible(ViewUI.Menu);
    this.nodeInfoVisible = this.layoutService.isVisible(ViewUI.NodeInfo);
    this.searchVisible = this.layoutService.isVisible(ViewUI.Search);
    this.titleOnlyVisible = this.layoutService.isVisible(ViewUI.TitleOnly);
    this.toolbarVisible = this.layoutService.isVisible(ViewUI.Toolbar);
    this.undoVisible = this.layoutService.isVisible(ViewUI.Undo);

    this.updateViewHeight();
    this.resizeEngineContainer();

    // Update label visibility status
    const enableAnatomyLabels = layout.anatomyLabels.enable !== false;
    const enableAnatomyInfo = layout.nodeInfo.enable !== false;
    const enableAnatomyActions = true;
    this.store.dispatch(
      new EnableContextLabel(
        enableAnatomyLabels,
        enableAnatomyInfo,
        enableAnatomyActions
      )
    );

    const enableLabels = layout.labels.enable !== false;
    this.store.dispatch(new ToggleLabels(enableLabels));

    // Add a timeout to allow drawer time to render
    setTimeout(() => {
      if (this.drawer) {
        // Update elements relative to the drawer
        this.positionRelativeElements(this.drawerY);
      } else {
        this.positionRelativeElements();
      }

      this.changeDetectorRef.markForCheck();
    });
  }

  positionRelativeElements(drawerOffsetY = 0) {
    const breakpoint = this.store.selectSnapshot(BreakpointState.getBreakpoint);

    // Offset navbar
    if (this.viewCameraBar != null && this.cameraVisible) {
      const navBarOffsetX = -4;
      const navBarOffsetY = this.menuVisible ? -64 : 0;

      this.renderer.setStyle(
        this.viewCameraBar.nativeElement,
        'transform',
        `translate3d(${navBarOffsetX}px, ${navBarOffsetY}px, 0)`
      );
    }

    // Offset toolbar
    // const toolbarOffsetX = isNavVisible ? -60 : -8;
    const toolbarOffsetX = -8;
    this.renderer.setStyle(
      this.viewToolbar.nativeElement,
      'transform',
      `translate3d(${toolbarOffsetX}px, 0, 0)`
    );

    // Offset base container bar
    // const baseOffsetY = (breakpoint > Breakpoint.Medium) ? -8 : this.drawerVisible ? (drawerOffsetY + drawerHeight) - 8 : -8;
    const baseOffsetY = -8;
    this.renderer.setStyle(
      this.viewSceneBase.nativeElement,
      'transform',
      `translate3d(0, ${baseOffsetY}px, 0)`
    );

    // Push drawer
    this.renderer.setStyle(
      this.view.nativeElement,
      'padding-bottom',
      `${-drawerOffsetY}px`
    );
  }

  isVisible(layout: ViewUI) {
    return this.layoutService.isVisible(layout);
  }

  isEnabled(layout: ViewUI) {
    return this.layoutService.isUIEnabled(layout);
  }

  setPageTitle(chapter: ChapterModel) {
    const chapterTitle = get(chapter, 'info.displayName');
    const title = chapterTitle ? `${chapterTitle} | ` : '';
    this.title.setTitle(`${title}BioDigital Human Platform`);
  }
}
